/**
 * WhiteLayer 3D Décor - Shopify Theme 2.0 Client Scripts
 * Handles AJAX Cart Drawer, LocalStorage Wishlist, Ambient Soundscape, and UI Interactions
 */

(() => {
  // =========================================================================
  // 1. Wishlist Sync
  // =========================================================================
  const WISHLIST_KEY = 'whitelayer:wishlist';

  const readWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  };

  const syncWishlistButtons = () => {
    const wishlist = readWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = button.getAttribute('data-product-handle');
      const active = wishlist.includes(handle);
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('is-active', active);
    });
  };

  // =========================================================================
  // 2. AJAX Cart & Slide-Over Drawer
  // =========================================================================
  const cartDrawer = document.getElementById('CartDrawer');
  const FREE_SHIPPING_THRESHOLD = 99900; // ₹999 in paise

  const formatMoney = (cents) => {
    const rupees = Math.round(cents / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  };

  const openCartDrawer = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeCartDrawer = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const updateCartDrawerUI = (cart) => {
    // 1. Update Header Badge Count
    document.querySelectorAll('[data-cart-count], [data-cart-drawer-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    // 2. Update Free Shipping Progress Bar
    const shippingBar = document.querySelector('[data-shipping-bar]');
    if (shippingBar) {
      const progress = Math.min(100, Math.round((cart.total_price / FREE_SHIPPING_THRESHOLD) * 100));
      const needed = Math.max(0, FREE_SHIPPING_THRESHOLD - cart.total_price);

      const fill = shippingBar.querySelector('[data-shipping-fill]');
      const percentText = shippingBar.querySelector('[data-shipping-percent]');
      const text = shippingBar.querySelector('[data-shipping-text]');

      if (fill) fill.style.width = progress + '%';
      if (percentText) percentText.textContent = progress + '%';
      if (text) {
        if (cart.total_price >= FREE_SHIPPING_THRESHOLD) {
          text.innerHTML = '<strong>Free delivery unlocked</strong>';
        } else {
          text.innerHTML = 'Add <strong>' + formatMoney(needed) + '</strong> for free delivery';
        }
      }
    }

    // 3. Render Line Items or Empty State
    const itemsContainer = document.getElementById('CartDrawerItems');
    const emptyContainer = document.getElementById('CartDrawerEmpty');
    const footerContainer = document.getElementById('CartDrawerFooter');
    const subtotalEl = document.querySelector('[data-cart-drawer-subtotal]');

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    if (cart.item_count === 0) {
      if (itemsContainer) itemsContainer.style.display = 'none';
      if (emptyContainer) emptyContainer.style.display = 'block';
      if (footerContainer) footerContainer.style.display = 'none';
    } else {
      if (emptyContainer) emptyContainer.style.display = 'none';
      if (footerContainer) footerContainer.style.display = 'block';
      if (itemsContainer) {
        itemsContainer.style.display = 'block';
        itemsContainer.innerHTML = cart.items
          .map((item) => {
            const imageHtml = item.image
              ? `<img src="${item.image}" alt="${item.title}" loading="lazy" />`
              : `<div class="cart-drawer__item-placeholder">3D</div>`;

            const variantHtml =
              item.variant_title && item.variant_title !== 'Default Title'
                ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>`
                : '';

            return `
              <div class="cart-drawer__item" data-line-key="${item.key}">
                <a href="${item.url}" class="cart-drawer__item-image">
                  ${imageHtml}
                </a>
                <div class="cart-drawer__item-details">
                  <div class="cart-drawer__item-header">
                    <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
                    <button type="button" class="cart-drawer__item-remove" data-cart-remove="${item.key}" aria-label="Remove item">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                  ${variantHtml}
                  <div class="cart-drawer__item-price-row">
                    <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
                    <div class="cart-drawer__qty-selector">
                      <button type="button" class="cart-drawer__qty-btn" data-cart-qty-change="-1" data-line-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
                      <span class="cart-drawer__qty-val">${item.quantity}</span>
                      <button type="button" class="cart-drawer__qty-btn" data-cart-qty-change="1" data-line-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
                    </div>
                  </div>
                </div>
              </div>
            `;
          })
          .join('');
      }
    }
  };

  const fetchCart = async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      updateCartDrawerUI(cart);
      return cart;
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  };

  const addToCart = async (formData) => {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        await fetchCart();
        openCartDrawer();
      } else {
        const data = await res.json();
        alert(data.description || 'Unable to add item to cart.');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  const changeCartItem = async (lineKey, quantity) => {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      });
      if (res.ok) {
        const cart = await res.json();
        updateCartDrawerUI(cart);
      }
    } catch (err) {
      console.error('Error changing cart item:', err);
    }
  };

  // =========================================================================
  // 3. Ambient Audio Soundscape
  // =========================================================================
  let audioCtx = null;
  let isPlayingSound = false;
  let oscillator1 = null;
  let oscillator2 = null;
  let gainNode = null;

  const initAmbientAudio = () => {
    if (audioCtx) return;
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      audioCtx = new AudioContextClass();

      // Generative 432Hz ambient drone chord (432Hz & 216Hz harmonic octave)
      oscillator1 = audioCtx.createOscillator();
      oscillator2 = audioCtx.createOscillator();
      gainNode = audioCtx.createGain();

      oscillator1.type = 'sine';
      oscillator1.frequency.setValueAtTime(432, audioCtx.currentTime); // A = 432Hz

      oscillator2.type = 'triangle';
      oscillator2.frequency.setValueAtTime(216, audioCtx.currentTime); // Sub octave

      gainNode.gain.setValueAtTime(0.0001, audioCtx.currentTime);

      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator1.start();
      oscillator2.start();
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  };

  const toggleSoundscape = () => {
    initAmbientAudio();
    const bar = document.getElementById('SoundscapeBar');
    const playIcon = document.getElementById('PlayIcon');
    const pauseIcon = document.getElementById('PauseIcon');

    if (!audioCtx || !gainNode) return;

    if (!isPlayingSound) {
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      gainNode.gain.cancelScheduledValues(audioCtx.currentTime);
      gainNode.gain.setTargetAtTime(0.04, audioCtx.currentTime, 0.5);
      isPlayingSound = true;
      if (bar) bar.classList.add('is-playing');
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
    } else {
      gainNode.gain.cancelScheduledValues(audioCtx.currentTime);
      gainNode.gain.setTargetAtTime(0.00001, audioCtx.currentTime, 0.3);
      isPlayingSound = false;
      if (bar) bar.classList.remove('is-playing');
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
    }
  };

  // =========================================================================
  // 4. Global Event Delegations
  // =========================================================================
  document.addEventListener('click', (event) => {
    // Wishlist Toggle
    const wishlistBtn = event.target.closest('[data-wishlist-toggle]');
    if (wishlistBtn) {
      event.preventDefault();
      const handle = wishlistBtn.getAttribute('data-product-handle');
      if (handle) {
        const wishlist = readWishlist();
        const next = wishlist.includes(handle)
          ? wishlist.filter((item) => item !== handle)
          : [...wishlist, handle];
        writeWishlist(next);
        syncWishlistButtons();
      }
      return;
    }

    // Soundscape Toggle
    if (event.target.closest('#GlobalSoundToggle')) {
      event.preventDefault();
      toggleSoundscape();
      return;
    }

    // Open Cart Drawer on Header Cart icon click
    const headerCartBtn = event.target.closest('.site-header__cart, [data-cart-trigger], .cart-icon');
    if (headerCartBtn) {
      event.preventDefault();
      openCartDrawer();
      return;
    }

    // Close Cart Drawer
    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeCartDrawer();
      return;
    }

    // Remove item from Cart Drawer
    const removeBtn = event.target.closest('[data-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      const lineKey = removeBtn.getAttribute('data-cart-remove');
      if (lineKey) changeCartItem(lineKey, 0);
      return;
    }

    // Quantity change inside Cart Drawer
    const qtyBtn = event.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      event.preventDefault();
      const delta = parseInt(qtyBtn.getAttribute('data-cart-qty-change'), 10);
      const lineKey = qtyBtn.getAttribute('data-line-key');
      const itemEl = qtyBtn.closest('.cart-drawer__item');
      const currentQtyEl = itemEl ? itemEl.querySelector('.cart-drawer__qty-val') : null;
      const currentQty = currentQtyEl ? parseInt(currentQtyEl.textContent, 10) : 1;
      const nextQty = Math.max(0, currentQty + delta);
      if (lineKey) changeCartItem(lineKey, nextQty);
      return;
    }
  });

  // Intercept form submissions for Add to Cart
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form && (form.action.includes('/cart/add') || form.getAttribute('action') === '/cart/add')) {
      event.preventDefault();
      const formData = new FormData(form);
      addToCart(formData);
    }
  });

  // Mobile menu toggle
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.getElementById('MobileNav');
  if (menuToggle && mobileNav) {
    menuToggle.addEventListener('click', () => {
      const expanded = menuToggle.getAttribute('aria-expanded') === 'true';
      menuToggle.setAttribute('aria-expanded', String(!expanded));
      mobileNav.hidden = expanded;
    });
  }

  // Keyboard shortcut: ESC closes cart drawer
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && cartDrawer && cartDrawer.classList.contains('is-open')) {
      closeCartDrawer();
    }
  });

  // Initialize state on DOM Ready
  syncWishlistButtons();

  if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches && 'IntersectionObserver' in window) {
    const revealTargets = document.querySelectorAll('.shopify-section:not(#shopify-section-main):not([data-no-rise])');
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('wl-rise');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    revealTargets.forEach((section) => revealObserver.observe(section));
  }
})();

